import {trackingEventActionsForAccounts} from "@buildwithflux/core";
import {FunctionsAdapter, ReactivateTrialResult} from "@buildwithflux/firebase-functions-adapter";
import {
    CurrentUser,
    UserStateType,
    UserUid,
    IUserData,
    IUserPrivateMetadata,
    StripeClientMode,
    TrialReactivationConfig,
} from "@buildwithflux/models";
import {PlanEntitlementService, isEligibleForTrialReactivation} from "@buildwithflux/plans";
import {Logger, wait} from "@buildwithflux/shared";
import type {FirebaseError} from "@firebase/util";

import {AnalyticsStorage} from "modules/storage_engine/AnalyticsStorage";
import {CurrentUserService} from "modules/auth/types";

/**
 * Function type for getting the trial reactivation config from feature flags
 * Returns undefined if the flag is not loaded yet (fail-safe behavior)
 */
type GetTrialReactivationConfig = () => TrialReactivationConfig | undefined;

export class TrialReactivationService {
    private hasAttemptedReactivationForUid: UserUid | undefined = undefined;

    constructor(
        private readonly currentUserService: CurrentUserService,
        private readonly functionsAdapter: FunctionsAdapter,
        private readonly planEntitlement: PlanEntitlementService,
        private readonly analyticsStorage: AnalyticsStorage,
        private readonly logger: Logger,
        private readonly stripeClientMode: StripeClientMode = StripeClientMode.enum.live,
        private readonly getTrialReactivationConfig?: GetTrialReactivationConfig,
    ) {
        this.currentUserService.subscribeToUserChanges((user) => this.onUserChanged(user));
    }

    /**
     * Listens for changes to the user, and attempts to reactivate the trial once a user is authenticated.
     * The backend performs the authoritative eligibility check before reactivating.
     */
    private onUserChanged(currentUser: CurrentUser & {suggestedState?: UserStateType}): void {
        const {user, privateMetadata, suggestedState} = currentUser;

        if (!user || !privateMetadata || (suggestedState && suggestedState !== "authenticated")) {
            return;
        }

        this.reactivateIfNeeded(user, privateMetadata);
    }

    /**
     * Attempt to reactivate the trial if the user is eligible
     *
     * This method implements fail-safe behavior: if the feature flag config is not loaded yet
     * (undefined), we proceed to call the backend anyway and let the backend be authoritative.
     * The frontend pre-check only blocks the backend call when we have a definitive "not eligible"
     * answer from the eligibility check.
     */
    private reactivateIfNeeded(user: IUserData, privateMetadata: IUserPrivateMetadata): void {
        if (this.hasAttemptedReactivationForUid === user.uid) {
            return;
        }

        // Get the feature flag config (may be undefined if not loaded yet)
        const config = this.getTrialReactivationConfig?.();

        // Only block the backend call if we have a definitive "not eligible" answer
        // If config is undefined (flag not loaded), we proceed to call the backend (fail-safe)
        if (config !== undefined) {
            const eligibilityResult = isEligibleForTrialReactivation(
                user,
                privateMetadata,
                this.planEntitlement,
                this.stripeClientMode,
                config,
            );
            if (!eligibilityResult.isEligible) {
                return;
            }
        }

        this.hasAttemptedReactivationForUid = user.uid;
        this.logger.info("Attempting trial reactivation for current user");
        this.attemptReactivation()
            .then((result) => {
                if (!result.trialReactivated && !result.creditBoostApplied) {
                    // User is not eligible for any promotion - this is expected, not an error
                    this.logger.debug("No promotion applied (user not eligible)", {result});
                    return;
                }

                this.logger.info("Trial promotion applied", {result});

                void this.analyticsStorage.trackAccountEvent(trackingEventActionsForAccounts.trialReactivated, user, {
                    result: "success",
                    promotionType: result.trialReactivated ? "trial_reactivation" : "credit_boost",
                });
            })
            .catch((e: unknown) => {
                this.logger.warn("Could not reactivate trial", {error: e});

                void this.analyticsStorage.trackAccountEvent(trackingEventActionsForAccounts.trialReactivated, user, {
                    result: "error",
                });
            });
    }

    /**
     * Inner function that calls the backend function to reactivate the trial
     *
     * Retries up to 3 times if we encounter FLUX-4073
     */
    private async attemptReactivation(attempt = 1): Promise<ReactivateTrialResult> {
        try {
            return await this.functionsAdapter.reactivateTrial();
        } catch (error: unknown) {
            if ((error as FirebaseError)?.code === "functions/internal" && attempt < 3) {
                /*
                 * This is some sort of CORS error, perhaps due to a lack of Access-Control-Allow-Credentials
                 * in the CORS response
                 *
                 * But as this is an onCall, Firebase is supposed to be handling the CORS response for us,
                 * and we don't really have control over it.
                 *
                 * All we can do is retry, which actually usually succeeds after some delay. This same handling
                 * has been applied elsewhere in the codebase, e.g. frontend/src/modules/payments/state/credit.ts:86
                 *
                 * TODO: FLUX-4073 upgrading to firebase-functions v2 in backend may help, or upgrading Firebase in general
                 */
                await wait(2_000);
                return await this.attemptReactivation(attempt + 1);
            }

            throw error;
        }
    }
}
